import classNames from 'classnames';
import React, {Fragment, ReactElement, ReactNode} from 'react';
import {useLocation} from 'react-router-dom';
import Icon from 'shared/components/icon';
import {ChecklistItem} from '../../../services/ChecklistService';
import MobileLink from '../../mobile-link/mobile-link';
import styles from './checklist-group.module.scss';
import {Flipped, Flipper} from 'react-flip-toolkit';
import {addSkipScrollingToRoute} from '../../../utils/routes';

interface Props {
  title?: ReactNode;
  subtitle?: ReactNode;
  list: ChecklistItem[];
}

const ChecklistGroup = ({title, subtitle, list}: Props): ReactElement => {
  const location = useLocation();

  const sortUncompletedFirst = (a: ChecklistItem, b: ChecklistItem): number => {
    if (a.completed === b.completed) {
      return 0;
    } else {
      return a.completed ? 1 : -1;
    }
  };

  const sortedList = list.filter((item) => !item.noLongerEarnable).sort(sortUncompletedFirst);

  return (
    <>
      {title && <p className={styles.title}>{title}</p>}
      {subtitle && <p className={styles.subtitle}>{subtitle}</p>}

      <ul className={styles.list}>
        <Flipper flipKey={sortedList}>
          {sortedList?.map((item) => {
            const hasLink = item.actionItemUrl && !item.completed;
            const Wrapper = hasLink ? MobileLink : Fragment;

            return (
              <Flipped key={item._id} flipId={item._id}>
                <li
                  key={item._id}
                  className={classNames(styles.item, {
                    [styles.hasLink]: hasLink,
                  })}
                  data-testid="checklist-item"
                >
                  <Wrapper
                    to={addSkipScrollingToRoute(`${location.pathname}${item.actionItemUrl || ''}`)}
                    className={classNames(styles.actionItemLink)}
                  >
                    {item.completed ? (
                      <Icon iconType="check-circle" className={classNames(styles.checkmark)} />
                    ) : (
                      <span>{/* I am here to balance out the grid please don't delete me*/}</span>
                    )}
                    <div className={classNames(styles.text)}>{item.name}</div>

                    <RewardSection
                      rewardText={item.rewardText}
                      rewardAllotmentUnit={item.rewardAllotmentUnit}
                    />
                    {item.actionItemUrl && !item.completed && (
                      <Icon
                        iconType="chevron-right"
                        color="grey"
                        className={classNames(styles.chevron)}
                      />
                    )}
                  </Wrapper>
                </li>
              </Flipped>
            );
          })}
        </Flipper>
      </ul>
    </>
  );
};

const RewardSection = ({
  rewardAllotmentUnit,
  rewardText,
}: Pick<ChecklistItem, 'rewardAllotmentUnit' | 'rewardText'>) => (
  <div className={styles.reward}>
    <span>{rewardText}</span>
    {rewardAllotmentUnit && (
      <span className={classNames(styles.rewardPerUnit)}>per {rewardAllotmentUnit}</span>
    )}
  </div>
);

export default ChecklistGroup;
